import { useEffect } from 'react';
import type { AnswerState, Question } from '../types';
import { norm, typeName } from '../utils';
import { ActionButtons } from './ActionButtons';

interface Props {
  question: Question;
  num: number;
  answerState?: AnswerState;
  selected: string[];
  blank: string;
  onSelectedChange: (v: string[]) => void;
  onBlankChange: (v: string) => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onFinish: () => void;
}

export function QuestionCard({
  question: q, num, answerState: st, selected, blank, onSelectedChange, onBlankChange,
  onSubmit, isFirst, isLast, onPrev, onNext, onFinish,
}: Props) {
  const answered = !!st;

  const keys = Object.keys(q.options || {});
  const correctSet = new Set(norm(q.answer).split(''));

  function toggleOption(k: string) {
    if (answered) return;
    if (q.type === 'single') {
      onSelectedChange([k]);
    } else {
      onSelectedChange(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);
    }
  }

  // 单选题键盘快捷键：字母键选择选项，Enter 提交
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (answered || q.type !== 'single') return;
      const key = e.key.toUpperCase();
      if (keys.includes(key)) onSelectedChange([key]);
      if (e.key === 'Enter') onSubmit();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const answerText = q.type === 'blank'
    ? q.answer
    : norm(q.answer).split('').map(k => `${k}. ${q.options?.[k] || ''}`).join('；');

  return (
    <div className="question-card">
      <div className="q-meta">
        <span className="tag">{typeName(q.type)}</span>
        {q.chapter ? <span className="tag chapter">{q.chapter}</span> : null}
        <span className="tag">题号 {q.id}</span>
      </div>
      <div className="q-title">{num}. {q.question}</div>
      {q.point ? <div className="q-point">知识点：{q.point}</div> : null}

      {q.type === 'blank' ? (
        <div>
          <input
            type="text"
            className={'blank-input' + (answered ? (st.ok ? ' right' : ' wrong') : '')}
            value={blank}
            disabled={answered}
            placeholder="请输入答案后点击「提交」"
            autoComplete="off"
            onChange={e => onBlankChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
          />
        </div>
      ) : (
        <div className="options">
          {keys.map(k => {
            const isSelected = answered ? st.answer.includes(k) : selected.includes(k);
            let cls = 'opt';
            if (answered) {
              if (correctSet.has(k)) cls += ' correct-opt';
              if (isSelected && !correctSet.has(k)) cls += ' wrong-opt';
            } else if (isSelected) {
              cls += ' selected';
            }
            const mark = answered && correctSet.has(k) ? '✓'
              : answered && isSelected && !correctSet.has(k) ? '✗' : '';
            return (
              <div
                key={k}
                className={cls}
                style={{ pointerEvents: answered ? 'none' : 'auto' }}
                onClick={() => toggleOption(k)}
              >
                <span className="key">{k}</span>
                <span className="text">{q.options[k]}</span>
                <span className="mark">{mark}</span>
              </div>
            );
          })}
        </div>
      )}

      {answered && (
        <div className={`feedback show ${st.ok ? 'ok' : 'no'}`}>
          <div>
            <b>{st.ok ? '✅ 回答正确' : '❌ 回答错误'}</b>　你的答案：<b>{st.answer || '（未作答）'}</b>　正确答案：<b>{answerText}</b>
          </div>
          {q.explanation ? (
            <>
              <div className="explain-title">📖 解释</div>
              <div>{q.explanation}</div>
            </>
          ) : null}
        </div>
      )}

      {/* 桌面端操作按钮显示在卡片内；移动端由底部操作栏渲染（CSS 隐藏此处） */}
      <ActionButtons
        isFirst={isFirst}
        isLast={isLast}
        answered={answered}
        onPrev={onPrev}
        onSubmit={onSubmit}
        onNext={onNext}
        onFinish={onFinish}
      />
    </div>
  );
}
